import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { useAuth } from '@/hooks/useAuth'
import { adminService, type StaffUser } from '@/services/adminService'
import { CreateStaffModal } from '@/components/admin/CreateStaffModal'

export interface AdminStaffPageProps {
  // Injected in tests; prod uses the module-level adminService.
  service?: typeof adminService
}

type TabKey = 'admin' | 'super_admin'

/**
 * AdminStaffPage (super_admin only) is the hub for staff management —
 * create admins / super admins, promote/demote, and toggle activation.
 *
 * Destructive actions (demote, deactivate) on the acting user's own row
 * are always disabled; the same actions on the sole remaining active
 * super admin are also disabled to preserve the "at least one active
 * super_admin" invariant.
 */
export const AdminStaffPage: React.FC<AdminStaffPageProps> = ({ service = adminService }) => {
  const { user } = useAuth()
  const [tab, setTab] = useState<TabKey>('admin')
  const [staff, setStaff] = useState<StaffUser[]>([])
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [modalRole, setModalRole] = useState<'admin' | 'super_admin' | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await service.listStaff()
      if (res.success && res.data) setStaff(res.data)
    } catch (err) {
      const e = err as Error
      setToast({ type: 'error', text: e.message || 'Gagal memuat staff' })
    } finally {
      setLoading(false)
    }
  }, [service])

  useEffect(() => {
    load()
  }, [load])

  const { filtered, activeSuperAdminIds } = useMemo(() => {
    const superSet = new Set<string>()
    for (const u of staff) {
      if (u.role === 'super_admin' && u.isActive) superSet.add(u.id)
    }
    const filtered = staff.filter((u) => u.role === tab)
    return { filtered, activeSuperAdminIds: superSet }
  }, [staff, tab])

  const isSelf = (id: string) => user?.id === id
  const isSoleActiveSuper = (u: StaffUser) =>
    u.role === 'super_admin' && u.isActive && activeSuperAdminIds.size <= 1

  const act = async (fn: () => Promise<unknown>, okMsg: string) => {
    try {
      await fn()
      setToast({ type: 'success', text: okMsg })
      await load()
    } catch (err) {
      const e = err as Error
      setToast({ type: 'error', text: e.message || 'Gagal menjalankan aksi' })
    }
  }

  return (
    <div data-testid="admin-staff-page">
      <PageHeader
        title="Kelola Staff"
        subtitle="Kelola admin dan super admin platform"
        actions={
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setModalRole('admin')}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-bold hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-primary/20"
              data-testid="add-admin-button"
            >
              <span className="material-symbols-outlined text-[20px]">person_add</span>
              Tambah Admin
            </button>
            <button
              type="button"
              onClick={() => setModalRole('super_admin')}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-900 dark:bg-slate-800 text-white text-sm font-bold hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-slate-900/20"
              data-testid="add-super-admin-button"
            >
              <span className="material-symbols-outlined text-[20px]">verified_user</span>
              Tambah Super Admin
            </button>
          </div>
        }
      />

      <div className="flex gap-2 mb-6">
        <button
          type="button"
          onClick={() => setTab('admin')}
          className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-all ${
            tab === 'admin' 
              ? 'bg-primary text-white shadow-md shadow-primary/20' 
              : 'bg-white dark:bg-surface-card text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/5 hover:text-slate-900 dark:hover:text-white'
          }`}
          data-testid="tab-admin"
        >
          Admin
        </button>
        <button
          type="button"
          onClick={() => setTab('super_admin')}
          className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-all ${
            tab === 'super_admin' 
              ? 'bg-primary text-white shadow-md shadow-primary/20' 
              : 'bg-white dark:bg-surface-card text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/5 hover:text-slate-900 dark:hover:text-white'
          }`}
          data-testid="tab-super-admin"
        >
          Super Admin
        </button>
      </div>

      {toast && (
        <div
          role={toast.type === 'error' ? 'alert' : 'status'}
          data-testid="staff-toast"
          className={`mb-4 p-3 rounded-lg ${
            toast.type === 'success'
              ? 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-300'
              : 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300'
          }`}
        >
          {toast.text}
        </div>
      )}

      <div className="rounded-lg bg-white dark:bg-surface-card border border-slate-100 dark:border-white/5 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-gray-100 dark:border-white/5">
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Nama</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Email</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted hidden md:table-cell">Peran</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted hidden md:table-cell">Status</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted hidden lg:table-cell">Dibuat</th>
                <th className="text-right px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-white/5">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-gray-500 dark:text-text-muted">
                    Memuat...
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-gray-500 dark:text-text-muted">
                    Belum ada {tab === 'admin' ? 'admin' : 'super admin'}.
                  </td>
                </tr>
              )}
              {filtered.map((u) => {
                const self = isSelf(u.id)
                const soleSuper = isSoleActiveSuper(u)
                return (
                  <tr
                    key={u.id}
                    className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                    data-testid={`staff-row-${u.id}`}
                  >
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white">{u.name}</td>
                    <td className="px-5 py-4 text-sm text-gray-500 dark:text-text-muted">{u.email}</td>
                    <td className="px-5 py-4 text-xs uppercase hidden md:table-cell">
                      <span
                        className={`px-2 py-1 rounded-full font-bold ${
                          u.role === 'super_admin'
                            ? 'bg-rose-500 text-white'
                            : 'bg-indigo-500 text-white'
                        }`}
                      >
                        {u.role}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-xs hidden md:table-cell">
                      <span
                        className={`px-2 py-1 rounded-full font-bold ${
                          u.isActive ? 'bg-emerald-500 text-white' : 'bg-slate-400 text-white'
                        }`}
                      >
                        {u.isActive ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-xs text-gray-500 dark:text-text-muted hidden lg:table-cell">
                      {new Date(u.createdAt).toLocaleDateString('id-ID')}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2 flex-wrap">
                        {u.role === 'admin' && (
                          <>
                            <Link
                              to="/admin/privileges"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-bold bg-slate-100 dark:bg-white/5 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-white/10 hover:bg-slate-200 dark:hover:bg-white/10 transition-all shadow-sm"
                              data-testid={`edit-perms-${u.id}`}
                            >
                              <span className="material-symbols-outlined text-[16px]">admin_panel_settings</span>
                              Izin
                            </Link>
                            <button
                              type="button"
                              onClick={() =>
                                act(() => service.promoteStaff(u.id), 'Berhasil dipromosikan')
                              }
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-bold bg-indigo-500 text-white hover:bg-indigo-600 transition-all shadow-sm"
                              data-testid={`promote-${u.id}`}
                            >
                              <span className="material-symbols-outlined text-[16px]">arrow_upward</span>
                              Promote
                            </button>
                          </>
                        )}
                        {u.role === 'super_admin' && (
                          <button
                            type="button"
                            disabled={self || soleSuper}
                            title={
                              self
                                ? 'Tidak bisa demote diri sendiri'
                                : soleSuper
                                  ? 'Satu-satunya super admin aktif'
                                  : ''
                            }
                            onClick={() =>
                              act(() => service.demoteStaff(u.id), 'Berhasil diturunkan ke admin')
                            }
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-bold bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20 hover:bg-orange-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                            data-testid={`demote-${u.id}`}
                          >
                            <span className="material-symbols-outlined text-[16px]">arrow_downward</span>
                            Demote
                          </button>
                        )}
                        {u.isActive ? (
                          <button
                            type="button"
                            disabled={self || soleSuper}
                            title={
                              self
                                ? 'Tidak bisa deactivate diri sendiri'
                                : soleSuper
                                  ? 'Satu-satunya super admin aktif'
                                  : ''
                            }
                            onClick={() =>
                              act(() => service.deactivateStaff(u.id), 'Berhasil dinonaktifkan')
                            }
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-bold bg-rose-500 text-white hover:bg-rose-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                            data-testid={`deactivate-${u.id}`}
                          >
                            <span className="material-symbols-outlined text-[16px]">block</span>
                            Nonaktifkan
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              act(() => service.reactivateStaff(u.id), 'Berhasil diaktifkan kembali')
                            }
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-bold bg-emerald-500 text-white hover:bg-emerald-600 transition-all shadow-sm"
                            data-testid={`reactivate-${u.id}`}
                          >
                            <span className="material-symbols-outlined text-[16px]">check_circle</span>
                            Aktifkan
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {modalRole && (
        <CreateStaffModal
          isOpen
          role={modalRole}
          onClose={() => setModalRole(null)}
          onSuccess={() => {
            setToast({ type: 'success', text: 'Staff berhasil dibuat' })
            load()
          }}
          service={service}
        />
      )}
    </div>
  )
}

export default AdminStaffPage
